import { useId, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { createQuote } from "../../lib/api/contractor";
import { ApiError } from "../../lib/api/client";
import { money } from "../../lib/format";
import type { Quote } from "../../lib/types/models";
import { PlusIcon, TrashIcon } from "@ui/react/icons";
import {
  Banner,
  Button,
  focusFirstInvalid,
  Modal,
  SelectField,
  TextArea,
  TextField,
} from "@ui/react";
import { errorMessage } from "../../lib/errorMessage";

interface LineItem {
  id: number;
  label: string;
  amount: string;
}

const ADD_LINE_ID = "send-quote-add-line";

let nextLineItemId = 1;

function blankLineItem(): LineItem {
  return { id: nextLineItemId++, label: "", amount: "" };
}

/** Paystack settles South African subaccounts in rands; the alternative is here
 *  only for the rare cross-border job, not as a general currency picker. */
const CURRENCIES = ["ZAR", "USD"] as const;

/** The three failures a contractor can actually do something about. Everything
 *  else falls through to the generic copy from `errorMessage`. */
function explain(error: unknown): { title: string; detail: string; needsPayout: boolean } {
  if (error instanceof ApiError) {
    if (error.needsPayoutSetup) {
      // The profile shows the payout account read-only and names support as
      // the way to change it (see the Payouts card in ProfileScreen), so this
      // banner points AT that card and never promises an "Add" the page
      // cannot do.
      return {
        title: "Your payout account isn't set up yet",
        detail:
          "Shoku pays you into the account on your profile, so the payout account has to exist before a quote can go out. Your profile shows what's on file; to add or change it, email shoku-support@businessblacksmiths.com.",
        needsPayout: true,
      };
    }
    if (error.code === "DUPLICATE_QUOTE") {
      return {
        title: "You've already quoted on this job",
        detail:
          "One quote per job. The homeowner still has your original — message them if the price has changed.",
        needsPayout: false,
      };
    }
    if (error.code === "WRONG_MODE") {
      return {
        title: "Switch to marketplace mode to quote",
        detail:
          "You're set up for direct bookings, which don't use quotes. Change your operating mode in booking setup first.",
        needsPayout: false,
      };
    }
  }
  return { title: "Couldn't send the quote", detail: errorMessage(error), needsPayout: false };
}

export function SendQuoteModal({
  jobId,
  onClose,
  onSent,
}: {
  jobId: string;
  onClose: () => void;
  onSent: (quote: Quote) => void;
}) {
  const formId = useId();
  const formRef = useRef<HTMLFormElement>(null);

  const [items, setItems] = useState<LineItem[]>(() => [blankLineItem()]);
  const [currency, setCurrency] = useState<string>("ZAR");
  const [description, setDescription] = useState("");
  const [estimatedDays, setEstimatedDays] = useState("1");
  const [validDays, setValidDays] = useState("");

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  // The total is DERIVED, never typed: it is the one number the customer sees,
  // so it must equal the itemisation the contractor just added up.
  const total = items.reduce((sum, item) => {
    const value = Number(item.amount);
    return Number.isFinite(value) && value > 0 ? sum + value : sum;
  }, 0);

  function updateItem(id: number, patch: Partial<LineItem>) {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  }

  function addItem() {
    setItems((current) => {
      const next = [...current, blankLineItem()];
      setAnnouncement(`Line item ${next.length} added. ${next.length} items.`);
      return next;
    });
  }

  function removeItem(id: number, position: number) {
    setItems((current) => {
      if (current.length <= 1) return current;
      const next = current.filter((item) => item.id !== id);
      setAnnouncement(`Line item ${position} removed. ${next.length} remaining.`);
      // This button unmounts with its own row, and focus would otherwise fall
      // to <body> — inside a modal that means the page behind the backdrop.
      requestAnimationFrame(() => document.getElementById(ADD_LINE_ID)?.focus());
      return next;
    });
  }

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};

    items.forEach((item) => {
      const isOnly = items.length === 1;
      const untouched = !item.label.trim() && !item.amount.trim();
      // A single blank row is just an unfilled form; a blank row among filled
      // ones is a mistake worth naming.
      if (untouched && !isOnly) {
        errors[`label:${item.id}`] = "Add a description or remove this line";
        return;
      }
      if (!item.label.trim()) errors[`label:${item.id}`] = "Describe what this covers";
      const value = Number(item.amount);
      if (!item.amount.trim()) {
        errors[`amount:${item.id}`] = "Enter an amount";
      } else if (!Number.isFinite(value) || value <= 0) {
        errors[`amount:${item.id}`] = "Enter an amount above zero";
      }
    });

    if (!description.trim()) errors["description"] = "Tell the homeowner what the work involves";

    const days = Number(estimatedDays);
    if (!estimatedDays.trim()) {
      errors["estimatedDays"] = "Enter how many days you need";
    } else if (!Number.isInteger(days) || days < 1) {
      errors["estimatedDays"] = "Must be at least 1 day";
    }

    if (validDays.trim()) {
      const valid = Number(validDays);
      if (!Number.isInteger(valid) || valid < 1) {
        errors["validDays"] = "Must be at least 1 day";
      }
    }

    return errors;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      focusFirstInvalid(formRef.current);
      return;
    }

    setSubmitting(true);
    try {
      const quote = await createQuote({
        jobId,
        // Line items are NOT sent: `CreateQuoteRequest` has no such field and
        // the server drops them silently. They exist purely as the contractor's
        // calculator for this figure.
        amount: total,
        currency,
        description: description.trim(),
        estimatedDays: Number(estimatedDays),
        ...(validDays.trim() ? { validDays: Number(validDays) } : {}),
      });
      onSent(quote);
    } catch (error) {
      setFormError(error);
    } finally {
      setSubmitting(false);
    }
  }

  const failure = formError ? explain(formError) : null;

  return (
    <Modal
      title="Send a quote"
      wide
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={submitting}>
            Send quote
          </Button>
        </>
      }
    >
      <form className="form" id={formId} ref={formRef} onSubmit={handleSubmit} noValidate>
        {failure ? (
          <Banner
            tone="danger"
            title={failure.title}
            action={
              failure.needsPayout ? (
                <Link className="btn btn-tertiary" to="/profile">
                  View payout details
                </Link>
              ) : undefined
            }
          >
            {failure.detail}
          </Banner>
        ) : null}

        <div className="stack stack--tight">
          <span className="cluster cluster--between">
            <span className="row__title">Price breakdown</span>
            <Button id={ADD_LINE_ID} variant="tertiary" onClick={addItem}>
              <PlusIcon />
              Add line
            </Button>
          </span>
          <span className="field__hint">
            The breakdown is your own arithmetic — it works out the total and is not sent to
            the homeowner. They see the total and the description below.
          </span>
        </div>

        {items.map((item, index) => (
          <div key={item.id} className="cluster cluster--start quote-line">
            <div className="quote-line__label">
              <TextField
                label={`Line ${index + 1} description`}
                placeholder="e.g. Labour"
                value={item.label}
                error={fieldErrors[`label:${item.id}`]}
                onChange={(event) => updateItem(item.id, { label: event.target.value })}
              />
            </div>
            <div className="quote-line__amount">
              <TextField
                label={`Line ${index + 1} amount`}
                inputMode="decimal"
                placeholder="0"
                value={item.amount}
                error={fieldErrors[`amount:${item.id}`]}
                onChange={(event) => updateItem(item.id, { amount: event.target.value })}
              />
            </div>
            {items.length > 1 ? (
              <button
                type="button"
                className="icon-btn quote-line__remove"
                aria-label={`Remove line ${index + 1}`}
                onClick={() => removeItem(item.id, index + 1)}
              >
                <TrashIcon />
              </button>
            ) : null}
          </div>
        ))}

        <span className="cluster cluster--between">
          <span className="muted">Quote total</span>
          <span className="amount">{money(total, currency)}</span>
        </span>

        <p className="sr-only" role="status">
          {announcement}
        </p>

        <div className="field-grid">
          <SelectField
            label="Currency"
            value={currency}
            onChange={(event) => setCurrency(event.target.value)}
          >
            {CURRENCIES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </SelectField>

          <TextField
            label="Estimated days"
            inputMode="numeric"
            required
            value={estimatedDays}
            error={fieldErrors["estimatedDays"]}
            onChange={(event) => setEstimatedDays(event.target.value)}
          />

          <TextField
            label="Valid for (days)"
            inputMode="numeric"
            // Matches the fallback in contractor-api's CreateQuote handler
            // (src/apis/contractor-api/handlers/quote/quote.go) — no named
            // constant there, so keep this in step by eye if that changes.
            hint="Leave blank and the quote stays valid for 7 days."
            value={validDays}
            error={fieldErrors["validDays"]}
            onChange={(event) => setValidDays(event.target.value)}
          />
        </div>

        <TextArea
          label="What the quote covers"
          required
          rows={4}
          placeholder="Describe the work, materials and anything the homeowner needs to arrange."
          value={description}
          error={fieldErrors["description"]}
          onChange={(event) => setDescription(event.target.value)}
        />
      </form>
    </Modal>
  );
}
